import { useMemo, useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';

const DISMISS_KEY = 'trial-banner-dismissed';

export function TrialBanner() {
  const { isOnTrial, trialEndDate, tier, baseTier } = useSubscription();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
  }, []);

  const daysLeft = useMemo(() => {
    if (!trialEndDate) return 0;
    return Math.max(0, Math.ceil((new Date(trialEndDate).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
  }, [trialEndDate]);

  if (!isOnTrial || dismissed || daysLeft === 0) return null;

  const urgent = daysLeft <= 3;
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  const fallbackLabel = baseTier === 'free' ? 'Free' : baseTier.charAt(0).toUpperCase() + baseTier.slice(1);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className={`w-full border-b ${urgent ? 'bg-destructive/10 border-destructive/30' : 'bg-primary/10 border-primary/30'}`}>
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className={`h-4 w-4 shrink-0 ${urgent ? 'text-destructive' : 'text-primary'}`} />
          <span className="truncate">
            <strong>{tierLabel} trial</strong> — {daysLeft} day{daysLeft === 1 ? '' : 's'} left.
            <span className="hidden sm:inline text-muted-foreground"> Reverts to {fallbackLabel} after expiry.</span>
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button asChild size="sm" variant={urgent ? 'destructive' : 'default'} className="h-7 text-xs">
            <Link to="/pricing">Upgrade</Link>
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleDismiss} aria-label="Dismiss">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
